import React, { useEffect, useRef, useState } from 'react';

import { IconButton } from '@components/button/Button';
import Spinner from '@components/spinner/Spinner';
import useNotify from '@hooks/notify';

import VoiceWaveform from './VoiceWaveform';

import { formatDuration } from '../../utils/voiceNote';

// Only one voice note plays at a time across the page.
let activeAudio: HTMLAudioElement | null = null;

const DURATION_TIMEOUT_MS = 3000;

// Chrome's MediaRecorder writes webm files without a duration. Seeking far
// past the end makes the browser work it out, after which we seek back.
const resolveDuration = (audio: HTMLAudioElement) =>
  new Promise<void>((resolve) => {
    const cleanups: (() => void)[] = [];
    const finish = () => {
      cleanups.forEach((cleanup) => cleanup());
      resolve();
    };
    const listen = (event: string, handler: () => void) => {
      audio.addEventListener(event, handler);
      cleanups.push(() => audio.removeEventListener(event, handler));
    };
    const timeout = window.setTimeout(finish, DURATION_TIMEOUT_MS);
    cleanups.push(() => window.clearTimeout(timeout));
    listen('error', finish);

    const onMetadata = () => {
      if (Number.isFinite(audio.duration)) {
        finish();
        return;
      }
      listen('durationchange', () => {
        if (!Number.isFinite(audio.duration)) return;
        audio.currentTime = 0;
        finish();
      });
      audio.currentTime = 1e101;
    };
    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) onMetadata();
    else listen('loadedmetadata', onMetadata);
  });

interface VoiceNotePlayerProps {
  // Local recordings pass a src; saved notes fetch a short-lived URL on play.
  src?: string;
  loadSrc?: () => Promise<string | undefined>;
  durationSec?: number;
  // List cards pass a title (file name + time); the save preview passes
  // waveform bars instead.
  title?: string;
  bars?: number[];
}

const VoiceNotePlayer: React.FC<VoiceNotePlayerProps> = ({
  src,
  loadSrc,
  durationSec,
  title,
  bars = []
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const isPreparedRef = useRef(false);
  const isMountedRef = useRef(true);
  const { createErrorAlert } = useNotify();

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSec ?? NaN);

  useEffect(() => {
    isMountedRef.current = true;
    const audio = audioRef.current;
    return () => {
      isMountedRef.current = false;
      if (activeAudio === audio) activeAudio = null;
    };
  }, []);

  const prepare = async (audio: HTMLAudioElement) => {
    if (!audio.getAttribute('src')) {
      const url = await loadSrc?.();
      if (!url) throw new Error('Voice note URL unavailable');
      // preload="none" would stop the browser before it reads metadata.
      audio.preload = 'auto';
      audio.src = url;
    }
    await resolveDuration(audio);
    if (Number.isFinite(audio.duration)) setDuration(audio.duration);
    isPreparedRef.current = true;
  };

  const play = async (isRetry = false): Promise<void> => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (!isPreparedRef.current) {
        setIsLoading(true);
        await prepare(audio);
      }
      if (activeAudio && activeAudio !== audio) activeAudio.pause();
      activeAudio = audio;
      await audio.play();
    } catch {
      // A removed draft or closed tab cancels playback; nothing to report.
      if (!isMountedRef.current) return;
      if (loadSrc && !isRetry) {
        // Saved-note URLs expire after 10 minutes; fetch a fresh one once.
        isPreparedRef.current = false;
        audio.removeAttribute('src');
        await play(true);
        return;
      }
      createErrorAlert('Could not play voice note. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canSeek = Number.isFinite(duration) && duration > 0;
  const timeLabel = `${formatDuration(currentTime)} / ${formatDuration(
    duration
  )}`;

  const playButton = isLoading ? (
    <div className="flex size-8 shrink-0 items-center justify-center">
      <Spinner className="size-5" />
    </div>
  ) : (
    <IconButton
      iconName={isPlaying ? 'voice-pause' : 'voice-play'}
      variant="transparent"
      size="none"
      iconSize="extralarge"
      className="shrink-0 !p-0"
      handleClick={() => (isPlaying ? audioRef.current?.pause() : play())}
      aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
    />
  );

  const audio = (
    <>
      {/* Voice notes have no caption track. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        preload={src ? 'metadata' : 'none'}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
      />
    </>
  );

  if (title !== undefined) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-grey30 px-4 py-3">
        {audio}
        {playButton}
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-gray-900">{title}</span>
          <span className="text-xs tabular-nums text-gray-500">
            {timeLabel}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-grey30 px-4 py-3">
      {audio}
      {playButton}
      <VoiceWaveform
        bars={bars}
        progress={canSeek ? currentTime / duration : 0}
        onSeek={(ratio) => {
          if (audioRef.current && canSeek) {
            audioRef.current.currentTime = ratio * duration;
          }
        }}
      />
      <span className="shrink-0 text-sm tabular-nums text-gray-500">
        {timeLabel}
      </span>
    </div>
  );
};

export default VoiceNotePlayer;
